import { useContext, useEffect, useMemo, useState, VFC } from 'react';

import { Collector, Creator } from '@/apis/aniftyBackend/users/interface';
import UserItemsSection from '@/containers/UserProfilePage/UserItemsSection';
import { Web3Context } from '@/contexts/Web3Provider/Web3Provider';
import { convertImgixURL } from '@/utils/imgixUtils';

import EditableHeaderImage from './EditableHeaderImage';
import ProfileInfoSection from './ProfileInfoSection';
import styles from './UserProfilePage.module.scss';
import CollectorList from '@/components/CollectorList';
import EmailSignupModal from '@/components/EmailSignupModal';
import fetchUserByID from '@/apis/aniftyBackend/users/fetchUserByID';

type Props = {
  user: Collector | Creator;
};

const UserProfilePage: VFC<Props> = ({ user: _user }: Props) => {
  const web3Context = useContext(Web3Context);
  const [user, setUser] = useState<Collector | Creator>(_user);
  const [showEmailModal, setShowEmailModal] = useState(false);

  // update header image url to reflect changes instantly
  const handleHeaderImageUpdate = (headerImageURL: string) => {
    setUser({
      ...user,
      headerImageURL,
    });
  };

  useEffect(() => {
    getEmailModalShowStatus();
  }, []);

  const getEmailModalShowStatus = async () => {
    const userInfo = await fetchUserByID(user.userID);
    setShowEmailModal(!userInfo.emailAddress && !sessionStorage.getItem("emailModalSeen"));
  };

  const headerImageURL = useMemo(() => {
    return user.headerImageURL
      ? convertImgixURL(user.headerImageURL, {
          w: 2760,
          h: 520,
          fit: 'crop',
          // auto: 'compress',
        })
      : '/images/user-profile-page/default-header-image.jpg';
  }, [user]);

  const isMyProfile =
    web3Context.currentAccountAddress &&
    web3Context.currentAccountAddress.toLowerCase() === user.userID;

  const myProfileContent = (
    <>
      <EditableHeaderImage
        currentHeaderImageURL={headerImageURL}
        onUpdated={handleHeaderImageUpdate}
      />
      {showEmailModal && <EmailSignupModal user={user} />}
    </>
  );

  return (
    <>
      {isMyProfile ? (
        myProfileContent
      ) : (
        <div className={styles.headerImageContainer}>
          <img
            src={headerImageURL}
            alt="header image"
            className={styles.headerImage}
          />
        </div>
      )}
      <div className="container">
        <ProfileInfoSection user={user} />
        <CollectorList user={user} />
        <section className={styles.myItemsSectionContainer}>
          <UserItemsSection user={user} />
        </section>
      </div>
    </>
  );
};

export default UserProfilePage;
